import { Query, Q } from '@nozbe/watermelondb';
import { useCallback, useMemo } from 'react';
import ContactsService from 'shared/db/services/Contact';
import { ContactModel } from 'shared/types/Contacts';
import { FILTER_TYPE } from 'shared/types/filter';

import { useImagesContext } from '@/context/ImagesContext';
import { databaseValidation } from '@/services/databaseValidation';
import { dbServiceLogger } from '@/services/logger/logger';

import { useDatabase } from './useDatabase';

interface Props {
    contactsToSelect?: Query<ContactModel>;
}

export const useFetchContactsFn = ({ contactsToSelect }: Props) => {
    const { getDatabase } = useDatabase();
    const { images, ImagesService } = useImagesContext();

    const database = useMemo(() => getDatabase(), [getDatabase]);

    const fetchContactsAvatars = useCallback(
        (contacts: ContactModel[]) => {
            return contacts.reduce<Record<string, string | undefined>>(
                (acc, contact) => {
                    acc[contact.id] = ImagesService.getSingleImageByEntityId(
                        images,
                        contact.id,
                    )?.imageURL;
                    return acc;
                },
                {},
            );
        },
        [images, ImagesService],
    );

    const fetchContacts = useCallback(
        async (query = '') => {
            const contactsService = new ContactsService({
                database,
                imageService: ImagesService,
                logger: dbServiceLogger,
                validation: databaseValidation,
            });

            let fetchContactsFn: Promise<ContactModel[]>;

            switch (true) {
                case Boolean(contactsToSelect): {
                    const [queries, sortQueries] =
                        contactsService.getFilterContactsQueries();

                    const searchQuery =
                        contactsService.getFilterContactsSearchLokiQuery({
                            searchText: query,
                            includeBusinessName: true,
                        });

                    fetchContactsFn = contactsToSelect!
                        .extend(searchQuery)
                        .extend(...queries)
                        .extend(...sortQueries)
                        .fetch();
                    break;
                }
                case Boolean(query): {
                    fetchContactsFn = contactsService.getFilteredContacts({
                        searchText: query,
                        filterType: FILTER_TYPE.LOKI,
                        includeBusinessName: true,
                    });
                    break;
                }
                default: {
                    fetchContactsFn = contactsService.getAll(
                        Q.sortBy('first_name', 'asc'),
                        Q.sortBy('last_name', 'asc'),
                    );
                }
            }

            return fetchContactsFn;
        },
        [ImagesService, contactsToSelect, database],
    );

    return { fetchContacts, fetchContactsAvatars };
};
